// @flow

import React from "react";
import { localize } from "redux-i18n";
import { Button, Modal, ModalBody, ModalHeader, ModalFooter } from "@components";
import AbsenceReasonsForm from "./absence-reasons-form";
import { formatDateTime } from "@transformers/date";
import type { AbsenceReason } from "@modules/service/reducer/absence-reasons/types";

type OwnProps = $Exact<{
  schema: ?Object,
  data: Object,
  onSubmit: (data: AbsenceReason) => void,
  onClose: () => void,
  providerId: number,
}>;

type Props = $Exact<{ ...OwnProps, ...PropsFromLocalize }>;

type State = $Exact<{
  formData: Object,
  initialData?: Object,
}>;

export class AbsenceReasonsEditModal extends React.Component<Props, State> {
  state = {
    formData: {},
    initialData: null,
  };

  constructor(props: Props) {
    super(props);
    // Class components needs constructor for accessing the SchemaForm's ref
    this.formRef = React.createRef();
  }

  componentDidMount() {
    if (this.props.data) {
      this.setState({ initialData: this.data, formData: this.data });
    }
  }

  submitForm = () => {
    this.formRef.current.submit();
  };

  handleChange = (formData: Object) => {
    this.setState({ formData });
  };

  handleSubmit = (formData: Object) => {
    this.props.onSubmit(formData);
  };

  get data() {
    return {
      ...this.props.data,
      ...this.props.data.content,
      nurseTransferEligibility: "ALWAYS",
      nurseTransferEligibilityAbsenceDuration: null,
    };
  }

  get updatedBy() {
    if (!this.data.updatedBy) {
      return this.props.t("N/A");
    }

    return `${this.data.updatedBy.firstName || ""} ${this.data.updatedBy.lastName || ""}`;
  }

  get updatedAt() {
    return this.data.updatedAt ? formatDateTime(this.data.updatedAt) : this.props.t("N/A");
  }

  get isDisabled() {
    return JSON.stringify(this.state.initialData) === JSON.stringify(this.state.formData);
  }

  render() {
    return (
      <Modal className="modal-normal">
        <ModalHeader className="d-flex justify-content-between" toggle={this.props.onClose}>
          {this.props.t("EDIT_ABSENCE_REASON_MODAL_TITLE", { providerId: this.props.providerId })}
        </ModalHeader>
        <ModalBody>
          <div className="mb-4 pl-2">
            <div className="d-flex gap-20 mb-1">
              <span className="text-color-charcoal">{`${this.props.t("LAST_UPDATED")}:`}</span>
              <span className="font-weight-bold">{this.updatedAt}</span>
            </div>
            <div className="d-flex gap-17 b-line pb-4">
              <span className="text-color-charcoal">{`${this.props.t("LAST_UPDATED_BY")}:`}</span>
              <span className="font-weight-bold">{this.updatedBy}</span>
            </div>
          </div>
          {this.props.schema && this.data && (
            <AbsenceReasonsForm
              ref={this.formRef}
              data={this.state.formData}
              schema={this.props.schema}
              onChange={this.handleChange}
              onSubmit={this.handleSubmit}
            />
          )}
        </ModalBody>
        <ModalFooter className="d-flex justify-content-end">
          <Button color="secondary" onClick={this.props.onClose}>
            {this.props.t("CANCEL")}
          </Button>
          <Button materialIcon="save" disabled={this.isDisabled} onClick={this.submitForm}>
            {this.props.t("SAVE")}
          </Button>
        </ModalFooter>
      </Modal>
    );
  }
}

export default localize()(AbsenceReasonsEditModal);
